import React from 'react';
import {
	Box,
	Grid,
	GlobalStyles,
	Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { CustomizedDataGrid } from 'components/customized_data_grid';
import { Column } from 'react-data-grid';
import LoaderComponent from 'components/Loader';

/* ─── Section 2 KPI Types & Component ────────────────────────────── */

export interface KpiItem {
	id: string;
	titleLine1: string;
	titleLine2: string;
	statusColor: string;
	actualValue: string;
	targetLabel: string;
	targetValue: string;
}

interface EDFCatalogingProps {
	kpiCardData: KpiItem[];
	heatmapTableData: HeatmapRow[];
	isLoading: boolean;
}

const KpiCard: React.FC<{ item: KpiItem; isLast: boolean }> = ({ item, isLast }) => (
	<Grid
		item
		xs={12}
		md={4}
		sx={{
			p: '18px 28px',
			borderRight: isLast ? 'none' : { md: '1px solid #4B79A7' },
			borderBottom: isLast ? 'none' : { xs: '1px solid #4B79A7', md: 'none' },
			display: 'flex',
			alignItems: 'center',
			justifyContent: 'space-between',
		}}
	>
		<Typography
			sx={{
				fontSize: '15px',
				fontWeight: 700,
				color: '#FFFFFF',
				lineHeight: 1.2,
				letterSpacing: '0.5px',
			}}
		>
			{item.titleLine1}<br />{item.titleLine2}
		</Typography>
		<Box
			sx={{
				width: 22,
				height: 22,
				borderRadius: '50%',
				backgroundColor: item.statusColor,
				mx: 2,
				flexShrink: 0,
			}}
		/>
		<Box sx={{ textAlign: 'center', minWidth: 100 }}>
			<Typography sx={{ fontSize: '24px', fontWeight: 700, color: '#FFFFFF', lineHeight: 1 }}>
				{item.actualValue}
			</Typography>
			<Box sx={{ borderBottom: '1.5px solid #3B7AC0', width: '100%', my: 0.75 }} />
			<Typography sx={{ fontSize: '11px', fontWeight: 500, color: '#E2E8F0', mb: 0.25, whiteSpace: 'nowrap' }}>
				{item.targetLabel}
			</Typography>
			<Typography sx={{ fontSize: '18px', fontWeight: 700, color: '#FFFFFF', lineHeight: 1 }}>
				{item.targetValue}
			</Typography>
		</Box>
	</Grid>
);

/* ─── Heatmap Data Types & Columns ───────────────────────────────── */

export interface HeatmapRow {
	id: number;
	domain: string;
	na: string;
	europe: string;
	latam: string;
	amesa: string;
	apac: string;
	total: string;
	isTotalRow?: boolean;
}

const getHeatmapBgColor = (valStr: string) => {
	const num = Number.parseFloat(valStr.replace('%', ''));

	if (Number.isNaN(num)) return '#FFFFFF';
	if (num >= 95) return '#74C67A';
	if (num >= 80) return '#BCE37E';
	if (num >= 65) return '#FDE576';
	if (num >= 40) return '#F9B273';
	return '#EE7676';
};

const renderHeatmapCell = (value: string, isTotalRow?: boolean) => {
	const bgColor = getHeatmapBgColor(value);

	return (
		<Box
			sx={{
				backgroundColor: `${bgColor} !important`,
				width: '100%',
				height: '100%',
				display: 'flex',
				alignItems: 'center',
				justifyContent: 'center',
				fontWeight: isTotalRow ? 700 : 500,
				fontSize: '13px',
				color: '#000000',
			}}
		>
			{value}
		</Box>
	);
};

const heatmapColumns: Column<HeatmapRow>[] = [
	{
		key: 'domain',
		name: 'Domain',
		minWidth: 180,
		formatter: ({ row }) => (
			<Box
				sx={{
					fontWeight: row.isTotalRow ? 700 : 500,
					fontSize: '13px',
					color: '#000000',
					pl: 1.5,
					display: 'flex',
					alignItems: 'center',
					height: '100%',
				}}
			>
				{row.domain}
			</Box>
		),
	},
	{
		key: 'na',
		name: 'North America',
		minWidth: 100,
		formatter: ({ row }) => renderHeatmapCell(row.na, row.isTotalRow),
	},
	{
		key: 'europe',
		name: 'Europe',
		minWidth: 90,
		formatter: ({ row }) => renderHeatmapCell(row.europe, row.isTotalRow),
	},
	{
		key: 'latam',
		name: 'LATAM',
		minWidth: 90,
		formatter: ({ row }) => renderHeatmapCell(row.latam, row.isTotalRow),
	},
	{
		key: 'amesa',
		name: 'AMESA',
		minWidth: 90,
		formatter: ({ row }) => renderHeatmapCell(row.amesa, row.isTotalRow),
	},
	{
		key: 'apac',
		name: 'APAC',
		minWidth: 90,
		formatter: ({ row }) => renderHeatmapCell(row.apac, row.isTotalRow),
	},
	{
		key: 'total',
		name: 'Total',
		minWidth: 90,
		formatter: ({ row }) => renderHeatmapCell(row.total, row.isTotalRow),
	},
];

const heatmapDefaultColumns = [
	'Domain',
	'North America',
	'Europe',
	'LATAM',
	'AMESA',
	'APAC',
	'Total',
];

const EDFCataloging: React.FC<EDFCatalogingProps> = ({
	kpiCardData,
	heatmapTableData,
	isLoading,
}) => {
	const theme = useTheme();
	const colors = theme.palette as any;

	return (
		<Box>
			<GlobalStyles
				styles={{
					/* Heatmap Table styling */
					'.heatmap-table .rdg': {
						border: '1px solid #777777 !important',
						borderRadius: '4px',
						maxWidth: '100% !important',
						overflowX: 'auto !important',
					},
					'.heatmap-table .rdg-row .rdg-cell': {
						padding: '0 !important',
						borderRight: '1px solid #777777 !important',
						borderBottom: '1px solid #777777 !important',
					},
					'.heatmap-table .rdg-header-row .rdg-cell': { 
						backgroundColor: '#FFFFFF !important',
						color: '#ffffff !important',
						fontWeight: '700 !important',
						padding: '0 !important',
						borderRight: '1px solid #777777 !important',
						borderBottom: '1px solid #777777 !important',
						borderTop: 'none !important',
						borderLeft: 'none !important',
						display: 'flex !important',
						alignItems: 'center !important',
						justifyContent: 'center !important',
						textAlign: 'center !important',
						fontSize: '13px !important',
					},
					'.heatmap-table .rdg-header-row .rdg-cell *': {
						color: '#FFFFFF !important',
						fontWeight: '700 !important',
						border: 'none !important',
						outline: 'none !important',
						boxShadow: 'none !important',
						textDecoration: 'none !important',
					},
				}}
			/>

			<Typography
				variant="widgetTitleNew"
				mb={2}
				sx={{
					color: colors.widget?.text?.title ?? '#003771',
					fontWeight: 600,
				}}
			>
				EDF Cataloging & data quality | July week 4
			</Typography>

			{isLoading ? (
				<LoaderComponent pt={20} />
			) : (
				<>
					<Grid container spacing={2}>
						<Grid item xs={12}>
							<Box
								sx={{
									backgroundColor: '#0B3C6F',
									borderRadius: '4px',
									overflow: 'hidden',
									boxShadow: '0 2px 6px rgba(0,0,0,0.12)',
								}}
							>
								<Grid container spacing={0}>
									{kpiCardData.map((item, index) => (
										<KpiCard
											key={item.id}
											item={item}
											isLast={index === kpiCardData.length - 1}
										/>
									))}
								</Grid>
							</Box>
						</Grid>
					</Grid>

					{/* Heatmap & Highlights Section (2:1 Grid Ratio - 66% / 33%) */}
					<Grid container spacing={2} sx={{ mt: 1 }}>
						{/* Left Div (66% / lg={8}): Heatmap Table */}
						<Grid item xs={12} lg={8} sx={{ display: 'flex', minWidth: 0 }}>
							<Box
								p={2.5}
								borderRadius="16px"
								className="heatmap-table"
								sx={{
									backgroundColor: '#FFFFFF',
									border: '1px solid #E0E0E0',
									boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
									width: '100%',
									maxWidth: '100%',
									overflow: 'hidden',
									display: 'flex',
									flexDirection: 'column',
									justifyContent: 'space-between',
								}}
							>
								<Typography
									sx={{
										fontSize: '18px',
										fontWeight: 700,
										color: '#003771',
										mb: 2,
									}}
								>
									EDF DATA ONBOARDING HEAT MAP
								</Typography>

								<CustomizedDataGrid
									tableColumns={heatmapColumns}
									tableData={heatmapTableData}
									tableType="edfDataOnboardingHeatmap"
									defaultColumnNames={heatmapDefaultColumns}
									listLength={heatmapTableData.length}
									isFetching={false}
									isRenderPagination={false}
									isShowMoreButton={false}
									autoSetDraggedColumns={false}
									isLighterTheme={true}
									rowHeight={34}
									headerRowHeight={38}
									isDateRangeFilter={false}
									isDisabled={false}
									showExport={false}
								/>

								<Typography
									sx={{
										fontSize: '12px',
										fontStyle: 'italic',
										color: '#666666',
										textAlign: 'right',
										mt: 1,
									}}
								>
									*Data as of July Wk 1, 2026
								</Typography>
							</Box>
						</Grid>

						{/* Right Div (33% / lg={4}): Highlights */}
						<Grid item xs={12} lg={4} sx={{ display: 'flex' }}>
							<Box
								sx={{
									backgroundColor: '#FFFFFF',
									borderRadius: '16px',
									border: '2px solid #0070C0',
									p: 2.5,
									width: '100%',
									boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
									display: 'flex',
									flexDirection: 'column',
								}}
							>
								{/* Header with lines */}
								<Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 2 }}>
									<Box sx={{ flex: 1, height: '1px', backgroundColor: '#0070C0' }} />
									<Typography
										sx={{
											px: 2,
											fontSize: '18px',
											fontWeight: 700,
											color: '#003771',
											letterSpacing: '1px',
										}}
									>
										HIGHLIGHTS
									</Typography>
									<Box sx={{ flex: 1, height: '1px', backgroundColor: '#0070C0' }} />
								</Box>

								{/* Content list */}
								<Typography sx={{ fontSize: '13px', fontWeight: 700, color: '#000000', mb: 1 }}>
									Onboarding: 82.1%
								</Typography>

								<Typography sx={{ fontSize: '13px', fontWeight: 700, color: '#0070C0', mb: 1 }}>
									297 program Requirements delivered from Feb- June (+118% vs prior SteerCo)
								</Typography>

								<Box sx={{ pl: 0.5, mb: 2 }}>
									<Typography sx={{ fontSize: '13px', color: '#000000', mb: 0.75, lineHeight: 1.4, display: 'flex', alignItems: 'flex-start' }}>
										<Box component="span" sx={{ fontWeight: 900, fontSize: '18px', lineHeight: 1, mr: 1 }}>•</Box>
										<span><strong>Productivity: Feb-Jul (5 months): 164 requirements delivered</strong> . <span style={{ color: '#0070C0', fontWeight: 600 }}>July results: 68 DIRs delivered (+23.6%).</span></span>
									</Typography>

									<Typography sx={{ fontSize: '13px', color: '#0070C0', fontWeight: 600, lineHeight: 1.4, display: 'flex', alignItems: 'flex-start' }}>
										<Box component="span" sx={{ fontWeight: 900, fontSize: '18px', lineHeight: 1, mr: 1, color: '#0070C0' }}>•</Box>
										<span>Backlog Q3 reduced significantly. Working on to getting all the Q4 requirements by July 31st</span>
									</Typography>
								</Box>

								<Typography sx={{ fontSize: '13px', fontWeight: 700, color: '#000000', mb: 0.5 }}>
									Key success factors: <span style={{ fontWeight: 400 }}>Productivity maximized after 4 months of new WoW</span>
								</Typography>

								<Box sx={{ pl: 0.5, mb: 2 }}>
									<Typography sx={{ fontSize: '13px', color: '#0070C0', fontWeight: 500, lineHeight: 1.4, mb: 0.5, display: 'flex', alignItems: 'flex-start' }}>
										<Box component="span" sx={{ fontWeight: 900, fontSize: '18px', lineHeight: 1, mr: 1, color: '#0070C0' }}>•</Box>
										<span>SLA enforcement and tracking</span>
									</Typography>

									<Typography sx={{ fontSize: '13px', color: '#0070C0', fontWeight: 500, lineHeight: 1.4, display: 'flex', alignItems: 'flex-start' }}>
										<Box component="span" sx={{ fontWeight: 900, fontSize: '18px', lineHeight: 1, mr: 1, color: '#0070C0' }}>•</Box>
										<span>DIR faster approvals / less errors</span>
									</Typography>
								</Box>

								<Typography sx={{ fontSize: '13px', color: '#000000', mb: 1.5, lineHeight: 1.4 }}>
									<strong>Additionally:</strong> Ongoing discussions on onboarding metrics adjustment;
								</Typography>

								<Typography sx={{ fontSize: '13px', fontWeight: 700, color: '#000000', mb: 0.5 }}>
									Cataloging %:
								</Typography>

								<Box sx={{ pl: 0.5 }}>
									<Typography sx={{ fontSize: '13px', color: '#000000', lineHeight: 1.4, display: 'flex', alignItems: 'flex-start' }}>
										<Box component="span" sx={{ fontWeight: 900, fontSize: '18px', lineHeight: 1, mr: 1 }}>•</Box>
										<span>90% goal challenged by lack of resources. Automation to be delivered by end of August to partially address gap.</span>
									</Typography>
								</Box>
							</Box>
						</Grid>
					</Grid>
				</>
			)}
		</Box>
	);
};

export default EDFCataloging;
